import { useState } from 'react';

import type { RecurringBillTemplateWithLines, RecurringFrequency } from '@dcc-erp/shared';
import { fmtDate, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import {
  useDeactivateRecurringTemplate,
  useGenerateRecurringDue,
  useRecurringTemplates,
} from '../../lib/queries/recurring-bills';
import { Can } from '../Can';
import { RecurringTemplateFormModal } from './RecurringTemplateFormModal';

// =============================================================================
// AP Phase C4 — Recurring vendor-bill templates tab + form modal.
//
// RecurringTemplatesTab: lists all templates (vendor, description, frequency,
// dayOfMonth, nextRunDate, isActive) + a create button + a "สร้างบิลครบกำหนด
// ตอนนี้" manual-generate button. All mutating actions are gated by
// `recurringbill.manage` via <Can>.
//
// RecurringTemplateFormModal: create/edit a template with vendor (native select),
// branch/dept, description, frequency, dayOfMonth, startDate, endDate?,
// paymentTermCode, vatRate, and a lines table (description/qty/unitPrice/expenseCode).
// =============================================================================

export interface RecurringTemplatesTabProps {
  companyId: string | undefined;
}

function frequencyLabel(f: RecurringFrequency): string {
  switch (f) {
    case 'MONTHLY':
      return 'รายเดือน';
    case 'QUARTERLY':
      return 'รายไตรมาส';
    case 'YEARLY':
      return 'รายปี';
  }
}

export function RecurringTemplatesTab({ companyId }: RecurringTemplatesTabProps) {
  const { data: templates = [], isPending, error } = useRecurringTemplates(companyId);
  const deactivate = useDeactivateRecurringTemplate(companyId);
  const generate = useGenerateRecurringDue(companyId);

  const [editingRow, setEditingRow] = useState<RecurringBillTemplateWithLines | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [generateResult, setGenerateResult] = useState<{
    generated: number;
    skipped: number;
  } | null>(null);

  const COLS = 8;

  async function handleGenerate() {
    const result = await generate.mutateAsync();
    setGenerateResult(result);
  }

  return (
    <div className="tbl-shell">
      <div className="filter-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="muted" style={{ fontSize: 13 }}>
          บิลประจำ (Recurring Bills) — Templates ที่ Active จะสร้างบิล PENDING ตามรอบโดยอัตโนมัติ
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Can permission="recurringbill.manage">
            <button
              type="button"
              className="btn"
              data-testid="btn-generate-due"
              disabled={generate.isPending || !companyId}
              onClick={() => {
                void handleGenerate();
              }}
            >
              <Icon name="check" size={14} />
              {generate.isPending ? 'กำลังสร้าง…' : 'สร้างบิลครบกำหนดตอนนี้'}
            </button>
            <button
              type="button"
              className="btn primary"
              data-testid="btn-add-template"
              onClick={() => setShowAdd(true)}
              disabled={!companyId}
            >
              <Icon name="plus" size={14} /> เพิ่ม Template
            </button>
          </Can>
        </div>
      </div>

      {generateResult && (
        <div
          data-testid="generate-result-banner"
          style={{
            background: 'rgba(34,197,94,0.08)',
            border: '1px solid rgba(34,197,94,0.3)',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>
            <Icon name="check" size={14} /> สร้างแล้ว <b>{generateResult.generated}</b> บิล · ข้ามไป{' '}
            <b>{generateResult.skipped}</b> รายการ
          </span>
          <button
            type="button"
            className="btn ghost"
            style={{ padding: '2px 6px', fontSize: 12 }}
            onClick={() => setGenerateResult(null)}
          >
            <Icon name="x" size={12} />
          </button>
        </div>
      )}

      <div className="tbl-wrap" style={{ maxHeight: 480 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>ผู้ขาย</th>
              <th>รายละเอียด</th>
              <th>ความถี่</th>
              <th className="num">วันที่ตัดรอบ</th>
              <th>รันครั้งถัดไป</th>
              <th>VAT (%)</th>
              <th>สถานะ</th>
              <th>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {isPending && (
              <tr>
                <td colSpan={COLS} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  กำลังโหลด…
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={COLS} className="neg" style={{ textAlign: 'center', padding: 20 }}>
                  โหลดไม่สำเร็จ: {error.message}
                </td>
              </tr>
            )}
            {!isPending && !error && templates.length === 0 && (
              <tr>
                <td colSpan={COLS} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ยังไม่มี Template — กด &ldquo;เพิ่ม Template&rdquo; เพื่อสร้าง
                </td>
              </tr>
            )}
            {templates.map((t) => (
              <tr key={t.id} data-testid={`recurring-row-${t.id}`}>
                <td>{t.vendorName ?? t.vendorId.slice(0, 8)}</td>
                <td>{t.description}</td>
                <td>
                  <span className="badge info">{frequencyLabel(t.frequency)}</span>
                </td>
                <td className="num tnum">{t.dayOfMonth}</td>
                <td className="muted">{fmtDate(t.nextRunDate)}</td>
                <td className="tnum">{toNumber(t.vatRate)}</td>
                <td>
                  {t.isActive ? (
                    <span className="badge positive">
                      <span className="bdot" /> Active
                    </span>
                  ) : (
                    <span className="badge neutral">ปิด</span>
                  )}
                </td>
                <td>
                  <Can permission="recurringbill.manage">
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        className="btn sm ghost"
                        style={{ height: 26, fontSize: 13 }}
                        title="แก้ไข"
                        onClick={() => setEditingRow(t)}
                      >
                        <Icon name="settings" size={14} />
                      </button>
                      {t.isActive && (
                        <button
                          className="btn sm ghost"
                          style={{ height: 26, fontSize: 13, color: 'var(--negative)' }}
                          title="ปิดใช้งาน"
                          disabled={deactivate.isPending}
                          onClick={() => {
                            if (confirm('ยืนยันปิดใช้งาน Template นี้?')) {
                              void deactivate.mutateAsync(t.id);
                            }
                          }}
                        >
                          <Icon name="x" size={14} />
                        </button>
                      )}
                    </div>
                  </Can>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="tbl-footer">
        <span>แสดง {templates.length} template</span>
      </div>

      {showAdd && companyId && (
        <RecurringTemplateFormModal
          companyId={companyId}
          mode="add"
          onClose={() => setShowAdd(false)}
        />
      )}
      {editingRow && companyId && (
        <RecurringTemplateFormModal
          companyId={companyId}
          mode="edit"
          initial={editingRow}
          onClose={() => setEditingRow(null)}
        />
      )}
    </div>
  );
}
